import styled from "styled-components";
import { COLORS } from "../../../const/colors";

export const ContactSection = styled.section`
  padding: 6rem 0 3rem;
  background: linear-gradient(135deg, ${COLORS.BLACK_10} 0%, transparent 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      ${COLORS.ORANGE_10} 0%,
      ${COLORS.DARK_BLUE_10} 50%,
      transparent 70%
    );
    top: 20%;
    right: -150px;
    z-index: -1;
  }

  @media screen and (min-width: 992px) {
    padding: 7rem 0 4rem;
  }
`;

export const ContactContainer = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 1.5rem;
  display: grid;
  gap: 2rem;
  row-gap: 4rem;

  @media screen and (min-width: 767px) {
    grid-template-columns: repeat(2, 1fr);
    justify-content: center;
    column-gap: 3rem;
  }

  @media screen and (min-width: 992px) {
    column-gap: 6rem;
  }
`;

export const ContactTitle = styled.h3`
  text-align: center;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 2rem;
  color: ${(props) => (props.light ? COLORS.WHITE : COLORS.BLACK)};
  position: relative;

  &::after {
    content: "";
    position: absolute;
    width: 50px;
    height: 2px;
    background: linear-gradient(
      to right,
      ${COLORS.ORANGE},
      ${COLORS.DARK_BLUE}
    );
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 2px;
  }
`;

export const ContactInfo = styled.div`
  display: grid;
  gap: 1.5rem;

  @media screen and (min-width: 576px) {
    grid-template-columns: minmax(280px, 1fr);
    justify-content: center;
  }
`;

export const ContactCard = styled.div`
  background-color: ${(props) =>
    props.light ? COLORS.DARK_SURFACE : COLORS.WHITE};
  padding: 1.5rem;
  border-radius: 1rem;
  text-align: center;
  box-shadow: 0 10px 30px ${COLORS.BLACK_10};
  transition: all 0.3s ease;
  position: relative;
  backdrop-filter: blur(10px);
  border: 1px solid ${COLORS.ORANGE_20};

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 1rem;
    padding: 1px;
    background: linear-gradient(135deg, ${COLORS.ORANGE}, ${COLORS.DARK_BLUE});
    -webkit-mask: linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px ${COLORS.BLACK_20};
  }
`;

export const ContactCardIcon = styled.div`
  font-size: 2.5rem;
  color: ${COLORS.ORANGE};
  margin-bottom: 1rem;

  i {
    background: linear-gradient(45deg, ${COLORS.ORANGE}, ${COLORS.DARK_BLUE});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

export const ContactCardTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: ${(props) => (props.light ? COLORS.WHITE : COLORS.BLACK)};
`;

export const ContactCardData = styled.span`
  font-size: 0.875rem;
  display: block;
  margin-bottom: 1.25rem;
  color: ${(props) => (props.light ? COLORS.LIGHT_GRAY : COLORS.DARK_BLUE)};
`;

export const ContactButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background-color: ${COLORS.ORANGE};
  color: ${COLORS.WHITE};
  padding: 0.75rem 1.25rem;
  border-radius: 0.75rem;
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: 0 4px 10px ${COLORS.ORANGE_30};
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      ${COLORS.WHITE_30},
      transparent
    );
    transition: all 0.6s ease;
  }

  &:hover {
    background-color: ${COLORS.DARK_BLUE};
    transform: translateY(-3px);
    box-shadow: 0 6px 15px ${COLORS.DARK_BLUE_30};

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px ${COLORS.ORANGE_30};
  }

  &:disabled {
    background-color: ${COLORS.LIGHT_GRAY};
    cursor: not-allowed;
    box-shadow: none;

    &:hover {
      transform: none;
    }
  }
`;

export const ContactButtonIcon = styled.div`
  font-size: 1.25rem;
  transition: transform 0.3s ease;

  ${ContactButton}:hover & {
    transform: translateX(3px);
  }
`;

export const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  padding: 0 0.5rem;

  @media screen and (min-width: 576px) {
    width: 100%;
    max-width: 450px;
    margin: 0 auto;
    padding: 0;
  }
`;

export const ContactFormDiv = styled.div`
  position: relative;
  margin-bottom: 2rem;
  height: 4rem;
`;

export const ContactFormTag = styled.label`
  position: absolute;
  top: -0.75rem;
  left: 1.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0.25rem 0.75rem;
  color: ${(props) => (props.light ? COLORS.WHITE : COLORS.BLACK)};
  background-color: ${(props) =>
    props.light ? COLORS.DARK_BLUE : COLORS.WHITE};
  border-radius: 0.5rem;
  z-index: 10;
  box-shadow: 0 2px 6px ${COLORS.BLACK_10};
`;

export const ContactFormInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 2px solid ${COLORS.ORANGE_30};
  background: ${(props) => (props.light ? COLORS.DARK_BLUE : COLORS.WHITE)};
  color: ${(props) => (props.light ? COLORS.WHITE : COLORS.BLACK)};
  outline: none;
  font-size: 0.95rem;
  padding: 1.5rem;
  border-radius: 0.75rem;
  z-index: 1;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  &:focus {
    border-color: ${COLORS.ORANGE};
    box-shadow: 0 0 0 4px ${COLORS.ORANGE_20};
  }

  &::placeholder {
    color: ${(props) => (props.light ? COLORS.LIGHT_GRAY : COLORS.DARK_BLUE)};
  }
`;

export const ContactTextArea = styled.textarea`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 2px solid ${COLORS.ORANGE_30};
  background: ${(props) => (props.light ? COLORS.DARK_BLUE : COLORS.WHITE)};
  color: ${(props) => (props.light ? COLORS.WHITE : COLORS.BLACK)};
  font-size: 0.95rem;
  outline: none;
  padding: 1.5rem;
  border-radius: 0.75rem;
  z-index: 1;
  resize: none;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  &:focus {
    border-color: ${COLORS.ORANGE};
    box-shadow: 0 0 0 4px ${COLORS.ORANGE_20};
  }

  &::placeholder {
    color: ${(props) => (props.light ? COLORS.LIGHT_GRAY : COLORS.DARK_BLUE)};
  }
`;

export const ContactFormArea = styled.div`
  position: relative;
  margin-bottom: 2rem;
  height: 11rem;
`;

export const ButtonEmail = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${COLORS.ORANGE};
  color: ${COLORS.WHITE};
  padding: 0.85rem 1.5rem;
  border-radius: 0.75rem;
  font-weight: 500;
  font-size: 1rem;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: 0 4px 10px ${COLORS.ORANGE_30};
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      ${COLORS.WHITE_30},
      transparent
    );
    transition: all 0.6s ease;
  }

  &:hover {
    background-color: ${COLORS.DARK_BLUE};
    transform: translateY(-3px);
    box-shadow: 0 6px 15px ${COLORS.DARK_BLUE_30};

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px ${COLORS.ORANGE_30};
  }

  &:disabled {
    background-color: ${COLORS.LIGHT_GRAY};
    cursor: not-allowed;
    box-shadow: none;

    &:hover {
      transform: none;
    }
  }
`;
